/**
 * Endpoint-derived workstream/task state. Inputs are decrypted entities plus the run rows this
 * browser can vouch for (receipts, transcripts, history) and rows mirrored by another trusted
 * device into workstream/task entities (`source: 'entity'`). Nothing here reads relay plaintext.
 *
 * Evidence order: a run this browser observed beats another device's claim about the same run.
 * With no evidence the state is `unknown`, never idle and never invented progress.
 */
import type { RunSummary, Task, TaskDetail, Workstream, WorkstreamDetail } from './types.js'

export const ENTITY_SOURCE = 'entity'

/** Runs the endpoint could enumerate; `known:false` means the run list itself was unavailable. */
export interface RunEvidence {
  runs: RunSummary[]
  known: boolean
}

const live = (r: RunSummary) =>
  r.status === 'running' || r.status === 'waiting' || r.status === 'compacting'
const observed = (r: RunSummary) => r.source !== ENTITY_SOURCE

/** Prefer runs this browser observed over another device's mirrored claim about the same id. */
export function mergeRuns(runs: RunSummary[]): RunSummary[] {
  const byId = new Map<string, RunSummary>()
  for (const r of runs) {
    const prior = byId.get(r.runId)
    if (!prior || (!observed(prior) && observed(r))) byId.set(r.runId, r)
  }
  return [...byId.values()]
}

/** Synthetic run rows from entities another device (or a rotation) recorded. */
export function entityRuns(streams: WorkstreamDetail[], tasks: TaskDetail[]): RunSummary[] {
  const rows = new Map<string, RunSummary>()
  for (const w of streams) {
    if (!w.liveRun) continue
    const taskId = tasks.find((t) => t.runId === w.liveRun?.runId)?.id
    rows.set(w.liveRun.runId, {
      runId: w.liveRun.runId,
      machineId: w.liveRun.machineId,
      status: w.liveRun.status,
      headSeq: 0,
      ...(w.liveRun.agent ? { agent: w.liveRun.agent } : {}),
      title: w.name,
      projectId: w.projectId,
      workstreamId: w.id,
      ...(taskId ? { taskId } : {}),
      updatedAt: w.updatedAt,
      source: ENTITY_SOURCE,
    })
  }
  for (const t of tasks) {
    if (!t.runId || rows.has(t.runId) || (t.status !== 'running' && t.status !== 'blocked'))
      continue
    const ws = streams.find((w) => w.id === t.workstreamId)
    rows.set(t.runId, {
      runId: t.runId,
      machineId: ws?.machineId ?? ws?.liveRun?.machineId ?? '',
      status: t.status === 'blocked' ? 'waiting' : 'running',
      headSeq: 0,
      title: t.title,
      projectId: t.projectId,
      ...(t.workstreamId ? { workstreamId: t.workstreamId } : {}),
      taskId: t.id,
      updatedAt: t.updatedAt,
      source: ENTITY_SOURCE,
    })
  }
  return [...rows.values()]
}

export interface WorkstreamProjection {
  state: Workstream['state']
  runId?: string
  liveRun: WorkstreamDetail['liveRun']
  tasks: Task[]
}

export function projectWorkstream(
  ws: WorkstreamDetail,
  tasks: TaskDetail[],
  evidence: RunEvidence,
): WorkstreamProjection {
  const runs = mergeRuns(evidence.runs)
  const taskRuns = new Set(tasks.map((t) => t.runId).filter((id): id is string => !!id))
  const mine = runs.filter(
    (r) =>
      r.workstreamId === ws.id ||
      r.runId === ws.liveRun?.runId ||
      (taskRuns.has(r.runId) && (!r.workstreamId || r.workstreamId === ws.id)),
  )
  const active = mine.filter(live)
  const lead = active.find((r) => r.status === 'waiting') ?? active[0]
  const liveRun: WorkstreamDetail['liveRun'] = lead
    ? {
        runId: lead.runId,
        status: lead.status,
        machineId: lead.machineId,
        agent: lead.agent ?? null,
      }
    : null
  const byRun = new Map(mine.map((r) => [r.runId, r]))
  const shown = tasks
    .filter((t) => t.workstreamId === ws.id)
    .map((t): Task => {
      const run = t.runId ? byRun.get(t.runId) : undefined
      let status: Task['status']
      if (run && live(run)) status = run.status === 'waiting' ? 'blocked' : 'running'
      // Recorded as running/blocked but no live evidence: say so instead of guessing an outcome.
      else if ((t.status === 'running' || t.status === 'blocked') && t.runId && !run)
        status = evidence.known ? 'unknown' : t.status
      else if (t.status === 'running' && run) status = 'unknown'
      else
        status =
          t.status === 'proposed' || t.status === 'done' || t.status === 'blocked'
            ? t.status
            : t.status === 'running'
              ? 'running'
              : 'ready'
      return { id: t.id, title: t.title, status, labels: t.labels }
    })
  const open = tasks.filter((t) => t.workstreamId === ws.id && t.status !== 'cancelled')
  let state: Workstream['state']
  if (active.some((r) => r.status === 'waiting') || shown.some((t) => t.status === 'blocked'))
    state = 'blocked'
  else if (active.length) state = 'live'
  else if (!evidence.known) state = 'unknown'
  else if (open.some((t) => t.status === 'landing')) state = 'landing'
  else if (open.length && open.every((t) => t.landedAt != null)) state = 'landed'
  else if (ws.liveRun && !mine.some((r) => r.runId === ws.liveRun?.runId)) state = 'unknown'
  else if (shown.some((t) => t.status === 'unknown')) state = 'unknown'
  else state = 'idle'
  return { state, ...(lead ? { runId: lead.runId } : {}), liveRun, tasks: shown }
}
